import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, PhoneOff } from 'lucide-react'
import {
  getCallChannel,
  type CallChannelMessage,
} from '@/hooks/useClickToCallChannel'

/**
 * Page `/{locale}/call` — entrée click-to-call.
 *
 * Contrat avec l'extérieur :
 *   /{locale}/call?number=+14385551234[&auto=1]
 *
 * Comportement :
 *   1. ping le BroadcastChannel pour voir si un onglet dashboard est ouvert
 *   2a. si pong → broadcast dial + tente window.close + écran succès
 *   2b. si busy → écran "appel en cours"
 *   2c. timeout 300 ms → navigate vers /dashboard?call=... (flow normal,
 *       PhoneTab gère l'auto-call via les query params)
 *
 * Cette page est derrière AuthGuard : un user non connecté est intercepté
 * et renvoyé vers /login?next=... ; après login, usePostLoginRedirect
 * reconstruit la cible /dashboard?call=...
 */

type State =
  | { kind: 'pinging' }
  | { kind: 'sent'; number: string }
  | { kind: 'busy' }
  | { kind: 'redirect' } // transitionnel, useEffect navigate

const PING_TIMEOUT_MS = 300

export function CallPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const [state, setState] = useState<State>({ kind: 'pinging' })
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    handledRef.current = true

    const number = searchParams.get('number') || searchParams.get('call')
    const auto = (searchParams.get('auto') ?? '1') === '1'

    if (!number) {
      navigate(`/${locale}/dashboard`, { replace: true })
      return
    }

    const channel = getCallChannel()
    if (!channel) {
      // Pas de BroadcastChannel (browser ancien) → flow normal
      redirectToDashboard(navigate, locale, number, auto)
      return
    }

    const correlationId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)

    let resolved = false
    const timeout = setTimeout(() => {
      if (resolved) return
      resolved = true
      channel.close()
      setState({ kind: 'redirect' })
      redirectToDashboard(navigate, locale, number, auto)
    }, PING_TIMEOUT_MS)

    const onMessage = (e: MessageEvent<CallChannelMessage>) => {
      const msg = e.data
      if (msg.correlationId !== correlationId) return
      if (msg.type !== 'pong' && msg.type !== 'busy') return
      if (resolved) return
      resolved = true
      clearTimeout(timeout)

      if (msg.type === 'busy') {
        channel.close()
        setState({ kind: 'busy' })
        return
      }

      // pong → un dashboard idle a répondu
      channel.postMessage({
        type: 'dial',
        number,
        auto,
        correlationId,
      } satisfies CallChannelMessage)

      setState({ kind: 'sent', number })

      // Pas de window.close() : les navigateurs le bloquent dès que le tab n'a
      // pas été ouvert via window.open() (URL collée, favori, lien externe).
      // L'user ferme l'onglet manuellement (raccourci affiché à l'écran).

      // On ferme le channel un peu plus tard pour laisser le dial passer
      window.setTimeout(() => channel.close(), 200)
    }

    channel.addEventListener('message', onMessage)
    channel.postMessage({
      type: 'ping',
      correlationId,
    } satisfies CallChannelMessage)
  }, [navigate, searchParams, locale])

  if (state.kind === 'pinging' || state.kind === 'redirect') {
    return (
      <Centered>
        <Spinner />
        <p className="text-white/70 text-sm">
          {t('clickToCall.preparing', "Préparation de l'appel…")}
        </p>
      </Centered>
    )
  }

  if (state.kind === 'busy') {
    return (
      <Centered>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/20 mb-4">
          <PhoneOff className="h-7 w-7 text-amber-300" />
        </div>
        <h2 className="text-lg font-semibold text-white mb-2">
          {t('clickToCall.busyTitle', 'Un appel est déjà en cours')}
        </h2>
        <p className="text-white/70 text-sm mb-2 max-w-sm">
          {t(
            'clickToCall.busyDescription',
            'Terminez l\'appel en cours dans votre onglet Callizy avant d\'en lancer un nouveau.',
          )}
        </p>
        <CloseHint />
      </Centered>
    )
  }

  // state.kind === 'sent'
  return (
    <Centered>
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/20 mb-4">
        <CheckCircle2 className="h-7 w-7 text-emerald-300" />
      </div>
      <h2 className="text-lg font-semibold text-white mb-2">
        {t('clickToCall.sentTitle', 'Appel lancé')}
      </h2>
      <p className="text-white/70 text-sm mb-2">
        {t('clickToCall.sentDescription', 'L\'appel vers {{number}} a été envoyé à votre onglet Callizy.', {
          number: state.number,
        })}
      </p>
      <CloseHint />
    </Centered>
  )
}

/**
 * Hints clavier : aucun bouton ne peut faire ces actions à cause des
 * restrictions navigateur (impossible de focus un autre onglet ni de fermer
 * un onglet ouvert manuellement). On informe l'user du raccourci à utiliser.
 */
function CloseHint() {
  const { t } = useTranslation()
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad/.test(navigator.platform)
  const switchShortcut = isMac ? '⌘ + `' : 'Ctrl + Tab'
  const closeShortcut = isMac ? '⌘ + W' : 'Ctrl + W'
  return (
    <div className="mt-2 space-y-1.5 text-white/50 text-xs">
      <p>
        <kbd className="rounded bg-white/10 border border-white/20 px-1.5 py-0.5 font-mono">
          {switchShortcut}
        </kbd>{' '}
        {t('clickToCall.switchHint', 'pour basculer vers votre onglet Callizy')}
      </p>
      <p>
        <kbd className="rounded bg-white/10 border border-white/20 px-1.5 py-0.5 font-mono">
          {closeShortcut}
        </kbd>{' '}
        {t('clickToCall.closeHint', 'pour fermer cet onglet')}
      </p>
    </div>
  )
}

function redirectToDashboard(
  navigate: ReturnType<typeof useNavigate>,
  locale: string,
  number: string,
  auto: boolean,
) {
  const params = new URLSearchParams()
  params.set('view', 'phone')
  params.set('call', number)
  if (auto) params.set('auto', '1')
  navigate(`/${locale}/dashboard?${params.toString()}`, { replace: true })
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A192F] px-4">
      <div className="text-center max-w-md">{children}</div>
    </div>
  )
}

function Spinner() {
  return (
    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
  )
}
